import { userStorage } from './userStorage';

// Cache configuration
const USER_CACHE_KEY = 'flik_user_cache';
const CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

interface CachedUserData {
  data: any;
  timestamp: number;
}

interface UserCacheEntry {
  [identity: string]: CachedUserData;
}

/**
 * User cache manager for handling multiple user data caching
 */
export class UserCacheManager {
  private static instance: UserCacheManager;
  private cache: UserCacheEntry = {};

  private constructor() {
    this.loadCacheFromStorage();
  }

  static getInstance(): UserCacheManager {
    if (!UserCacheManager.instance) {
      UserCacheManager.instance = new UserCacheManager();
    }
    return UserCacheManager.instance;
  }

  /**
   * Load cache from storage on initialization
   */
  private loadCacheFromStorage(): void {
    try {
      const stored = userStorage.get<UserCacheEntry>(USER_CACHE_KEY);
      if (stored) {
        this.cache = stored;
      }
    } catch (error) {
      console.error('Failed to load user cache from storage:', error);
      this.cache = {};
    }
  }

  /**
   * Save cache to storage
   */
  private saveCacheToStorage(): void {
    try {
      userStorage.set(USER_CACHE_KEY, this.cache);
    } catch (error) {
      console.error('Failed to save user cache to storage:', error);
    }
  }

  /**
   * Check if cached data is still valid
   */
  private isCacheValid(timestamp: number): boolean {
    return Date.now() - timestamp < CACHE_EXPIRY_MS;
  }

  /**
   * Get user data by identity
   */
  getUserData(identity: string): any | null {
    const cached = this.cache[identity];
    if (cached && this.isCacheValid(cached.timestamp)) {
      return cached.data;
    }
    return null;
  }

  /**
   * Set user data for an identity
   */
  setUserData(identity: string, data: any): void {
    this.cache[identity] = {
      data,
      timestamp: Date.now(),
    };
    this.saveCacheToStorage();
  }

  /**
   * Set multiple user data entries
   */
  setMultipleUserData(users: Array<{ identity: string; data: any }>): void {
    const now = Date.now();
    users.forEach(({ identity, data }) => {
      this.cache[identity] = {
        data,
        timestamp: now,
      };
    });
    this.saveCacheToStorage();
  }

  /**
   * Check if user data is cached and valid
   */
  hasValidUserData(identity: string): boolean {
    const cached = this.cache[identity];
    return cached ? this.isCacheValid(cached.timestamp) : false;
  }

  /**
   * Get all cached user identities
   */
  getCachedIdentities(): string[] {
    return Object.keys(this.cache).filter(
      (identity) =>
        this.cache[identity] &&
        this.isCacheValid(this.cache[identity].timestamp),
    );
  }

  /**
   * Remove user data from cache
   */
  removeUserData(identity: string): void {
    delete this.cache[identity];
    this.saveCacheToStorage();
  }

  /**
   * Clear all cached user data
   */
  clearAllUserData(): void {
    this.cache = {};
    this.saveCacheToStorage();
  }

  /**
   * Clear expired entries
   */
  clearExpiredEntries(): void {
    const now = Date.now();
    Object.keys(this.cache).forEach((identity) => {
      if (!this.isCacheValid(this.cache[identity].timestamp)) {
        delete this.cache[identity];
      }
    });
    this.saveCacheToStorage();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    totalEntries: number;
    validEntries: number;
    expiredEntries: number;
  } {
    const totalEntries = Object.keys(this.cache).length;
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    Object.values(this.cache).forEach((entry) => {
      if (this.isCacheValid(entry.timestamp)) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    });

    return { totalEntries, validEntries, expiredEntries };
  }
}

// Export singleton instance
export const userCacheManager = UserCacheManager.getInstance();

/**
 * Clear all user data cache - should be called on logout
 */
export function clearUserDataCache(): void {
  userCacheManager.clearAllUserData();
}
